import React, { useEffect, useState } from "react";
import { Leads } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

const STATUS_COLOR = {
  new: "bg-atc-blue/15 text-atc-blue",
  contacted: "bg-amber-500/15 text-amber-300",
  quoted: "bg-violet-500/15 text-violet-300",
  won: "bg-atc-green/15 text-atc-green",
  lost: "bg-slate-500/15 text-slate-400",
};

const LeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setLeads(await Leads.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lead, status) => {
    try {
      await Leads.updateStatus(lead.id, status);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      if (selected?.id === lead.id) setSelected({ ...lead, status });
    } catch { toast.error("Couldn't update status"); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    try {
      await Leads.remove(lead.id);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      if (selected?.id === lead.id) setSelected(null);
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-10" data-testid="leads-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">CRM</div>
        <h1 className="font-display font-black text-4xl text-white">Leads</h1>
        <p className="text-slate-400 text-sm mt-2">Every quote request from the site lands here.</p>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400">No leads yet. They'll show up here.</div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 glass rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Service</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className={`cursor-pointer hover:bg-slate-900/60 ${selected?.id === l.id ? "bg-slate-900/80" : ""}`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="text-white">{l.name}</div>
                      <div className="text-xs text-slate-500">{l.email}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{l.service || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${STATUS_COLOR[l.status] || ""}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="lg:col-span-5">
            {selected ? (
              <div className="glass rounded-2xl p-6 sticky top-6" data-testid="lead-detail">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Lead</div>
                    <div className="font-display font-bold text-2xl text-white mt-1">{selected.name}</div>
                  </div>
                  <button onClick={() => remove(selected)} className="text-slate-400 hover:text-rose-400" data-testid="lead-delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-5 space-y-2 text-sm">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-slate-200 hover:text-white">
                    <Mail className="h-4 w-4" /> {selected.email}
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-slate-200 hover:text-white">
                      <Phone className="h-4 w-4" /> {selected.phone}
                    </a>
                  )}
                </div>
                {selected.package_name && (
                  <div className="mt-4 text-xs text-slate-400">Package: <span className="text-white">{selected.package_name}</span></div>
                )}
                {selected.service && (
                  <div className="mt-1 text-xs text-slate-400">Service: <span className="text-white">{selected.service}</span></div>
                )}
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Message</div>
                  <div className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-950/60 border border-white/5 rounded-lg p-4">
                    {selected.message || "—"}
                  </div>
                </div>
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Status</div>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected, v)}>
                    <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-white">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="glass rounded-2xl p-8 text-center text-slate-500 text-sm">Select a lead to see details.</div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
